import "./RiskManagementComponent.scss";
import {IOnDestroy, IOnInit, IScope} from "angular";
import {
    defaultSortOptions,
    initialMarginAscending,
    initialMarginDescending,
    marginAscending,
    marginDescending,
    RiskSortOptions,
    stelAscending,
    stelDescending
} from "../../models/sorting/RiskSortOptions";
import {NotificationType} from "../notifications/NotificationType";
import {Optional} from "../../../../js/util/Optional";
import {buildNameFilter} from "../../models/filtering/NameFilter";
import {SortingColumn, SortingDefinition} from "./models/Sorting";
import {RiskDashboardViewStore} from "../../store/RiskDashboardViewStore";
import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {RiskRowEntityType} from "../../models/RiskRowEntityType";
import {ViewModelRow} from "../../models/view-model/RiskDashboardViewModel";

export const RiskManagementComponent = "riskManagement";

export function riskManagementComponent() {
    return {
        templateUrl: "app/risk/dashboard/components/grid/RiskManagementComponent.html",
        bindings: {
            viewStore: "<"
        },
        controllerAs: "ctrl",
        controller: RiskManagementComponentController
    };
}

export class RiskManagementComponentController implements IOnInit, IOnDestroy {

    public readonly viewStore: RiskDashboardViewStore;

    public rootLevel: Observable<RiskRowEntityType>;
    public rows: Observable<ReadonlyArray<ViewModelRow>>;

    public filterString: string;
    public currentSort: SortingDefinition | null = null;
    public userSort: SortingDefinition | null = null;

    private readonly sortOptionsChanged$ = new ReplaySubject<boolean>(1);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private $scope: IScope) {

    }

    public $onInit(): void {
        this.viewStore
            .activeNotification
            .takeUntil(this.destroyed$)
            .subscribe((notification) => this.applyNotificationSorting(notification));
        this.sortOptionsChanged$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map(() => this.buildSortingOptions())
            .subscribe((options) => this.viewStore.updateSorting(options));
        this.rootLevel = this.viewStore
                             .model
                             .takeUntil(this.destroyed$)
                             .map((model) => model.rootLevel);
        this.rows = this.viewStore
                        .model
                        .takeUntil(this.destroyed$)
                        .map((model) => model.rows);
    }

    private sortingChanged() {
        this.sortOptionsChanged$.next(true);
    }

    public updateFilter(value: string | null) {
        const nameFilter = buildNameFilter(value);
        this.viewStore.updateNameFilter(nameFilter);
    }

    public applyNotificationSorting(notificationType: NotificationType | null) {
        // Reset sort back to how it was before the preset was applied
        if (notificationType == null) {
            this.currentSort = this.userSort;
        } else {
            this.currentSort = this.createSortOptionsForNotification(notificationType);
        }
        this.sortingChanged();
    }

    public createSortOptionsForNotification(notificationType: NotificationType): SortingDefinition {
        switch (notificationType) {
            case NotificationType.ADVISER_STEL:
                return {
                    column: SortingColumn.STEL,
                    ascending: false
                };
            case NotificationType.CLIENT_STEL:
                return {
                    column: SortingColumn.STEL,
                    ascending: false
                };
            case NotificationType.CLIENT_NO_MARGIN:
                return {
                    column: SortingColumn.MARGIN,
                    ascending: false
                };
            case NotificationType.CLIENT_MARGIN_BREACH:
                return {
                    column: SortingColumn.MARGIN,
                    ascending: false
                };
            case NotificationType.CLIENT_INITIAL_MARGIN:
                return {
                    column: SortingColumn.INITIAL_MARGIN,
                    ascending: false
                };
        }
    }

    public toggleSorting(column: SortingColumn) {
        if (this.currentSort == null) {
            this.currentSort = {
                column,
                ascending: false
            };
        } else if (this.currentSort.column !== column) {
            this.currentSort = {
                column,
                ascending: false
            };
        } else if (!this.currentSort.ascending) {
            this.currentSort = {
                column: this.currentSort.column,
                ascending: true
            };
        } else {
            this.currentSort = null;
        }

        this.userSort = this.currentSort;
        this.sortingChanged();
    }

    private buildSortingOptions(): RiskSortOptions {
        return Optional.of(this.currentSort)
                       .map((s) => this.calculateSortOptions(s))
                       .orElse(defaultSortOptions());
    }

    private calculateSortOptions(sortingDefinition: SortingDefinition): RiskSortOptions {
        switch (sortingDefinition.column) {
            case SortingColumn.STEL:
                return sortingDefinition.ascending ? stelAscending() : stelDescending();
            case SortingColumn.INITIAL_MARGIN:
                return sortingDefinition.ascending ? initialMarginAscending() : initialMarginDescending();
            case SortingColumn.MARGIN:
                return sortingDefinition.ascending ? marginAscending() : marginDescending();
            default:
                return defaultSortOptions();
        }
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
